import { useMemo, useState } from 'react';
import { ActivityIndicator, ScrollView } from 'react-native';
import { useQueries } from '@tanstack/react-query';
import { Redirect, useGlobalSearchParams } from 'expo-router';
import { StyleSheet } from 'react-native-unistyles';
import { useLeagueContext } from '@/contexts/LeagueContext';
import MarchMadnessLeagueView from '@/components/MarchMadnessLeagueView';
import { TournamentBracketView } from '@/components/MarchMadnessBracketView';
import AppHeader from '@/components/AppHeader';
import NativeTabFocusGate from '@/components/navigation/NativeTabFocusGate';
import { Box, Text } from '@/components/ui/primitives';
import { getLandingHref, getLeagueHref } from '@/lib/routes';
import { apiRequestJson } from '@/lib/queryClient';
import { useTheme } from '@/lib/styling';
import { Theme } from '@/theme';
import type { LeagueWeekGamesPayload } from '@/hooks/useLeague';
import { buildWorldCupBracket } from '@shared/worldCupBracket';

export default function LeagueBracketRoute() {
  const routeParams = useGlobalSearchParams<{
    season?: string | string[];
    week?: string | string[];
  }>();
  const context = useLeagueContext();
  const theme = useTheme<Theme>();
  const [compactRoundKey, setCompactRoundKey] = useState<string | null>(null);
  const leagueId = context.id && context.id !== 'undefined' ? context.id : '';

  const { league, resolvedSeasonId, seasonWeekOptions, isWorldCupTournament } = context;

  const headerTitle = league?.name ?? 'League';
  const seasonParam = Array.isArray(routeParams.season) ? routeParams.season[0] : routeParams.season;
  const weekParam = Array.isArray(routeParams.week) ? routeParams.week[0] : routeParams.week;
  const worldCupQueries = useQueries({
    queries:
      leagueId && isWorldCupTournament && resolvedSeasonId
        ? seasonWeekOptions.map((week) => ({
            queryKey: ['/api/leagues', leagueId, 'weeks', week.id, resolvedSeasonId, 'bracket'],
            queryFn: () =>
              apiRequestJson<LeagueWeekGamesPayload>(
                `/api/leagues/${leagueId}/weeks/${week.id}/games?seasonId=${resolvedSeasonId}`,
                'GET',
              ),
            staleTime: 30_000,
          }))
        : [],
  });

  const worldCupBracket = useMemo(() => {
    if (!leagueId || !isWorldCupTournament || !resolvedSeasonId) {
      return null;
    }

    return buildWorldCupBracket({
      leagueId,
      seasonId: resolvedSeasonId,
      weeks: seasonWeekOptions,
      gamesByWeekId: new Map(
        seasonWeekOptions.map((week, index) => [
          week.id,
          (worldCupQueries[index]?.data?.games ?? []).map((entry) => entry.game),
        ]),
      ),
    });
  }, [leagueId, isWorldCupTournament, resolvedSeasonId, seasonWeekOptions, worldCupQueries]);

  const worldCupLoading = isWorldCupTournament && worldCupQueries.some((query) => query.isLoading);
  const worldCupError = isWorldCupTournament
    ? worldCupQueries.find((query) => query.error)?.error
    : null;

  if (!leagueId) {
    return <Redirect href={getLandingHref()} />;
  }

  return (
    <NativeTabFocusGate>
      <Box flex={1} backgroundColor="background">
        <AppHeader title={headerTitle} currentLeagueId={leagueId} allowBack={false} />
        <Box flex={1}>
          {context.isMarchMadness ? (
            <MarchMadnessLeagueView
              leagueId={leagueId}
              seasonId={resolvedSeasonId || ''}
              section="bracket"
              currentUserId={context.league?.currentUserId}
              permissions={context.permissions}
            />
          ) : isWorldCupTournament ? (
            worldCupLoading ? (
              <Box flex={1} alignItems="center" justifyContent="center" gap="m">
                <ActivityIndicator size="large" color={theme.colors.press} />
                <Text variant="body" color="mutedForeground">Loading bracket...</Text>
              </Box>
            ) : worldCupError ? (
              <Box flex={1} alignItems="center" justifyContent="center" padding="xl">
                <Text variant="body" color="destructive" textAlign="center">
                  Failed to load bracket.
                </Text>
              </Box>
            ) : worldCupBracket ? (
              <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent(theme.spacing.m)}>
                <TournamentBracketView
                  bracket={worldCupBracket}
                  compactRoundKey={compactRoundKey}
                  onSelectCompactRound={setCompactRoundKey}
                />
              </ScrollView>
            ) : null
          ) : (
            <Redirect
              href={getLeagueHref(leagueId, 'picks', {
                seasonId: seasonParam ?? null,
                weekId: weekParam ?? null,
              })}
            />
          )}
        </Box>
      </Box>
    </NativeTabFocusGate>
  );
}

const styles = StyleSheet.create(() => ({
  scrollContent: (padding: number) => ({
    padding,
    paddingBottom: 100,
  }),
  scrollView: {
    flex: 1,
  },
}));
